import { motion } from "framer-motion";
import { useState } from "react";
import type { Certification } from "../../types";
import { parseDateString } from "../../lib/dates";
import type { TimelineScales } from "../../hooks/useTimelineScales";

interface CertificationMarkerProps {
  certification: Certification;
  scales: TimelineScales;
  y: number;
  onHover: (cert: Certification | null, anchor: { x: number; y: number } | null) => void;
}

const SIZE = 7;

export function CertificationMarker({
  certification,
  scales,
  y,
  onHover,
}: CertificationMarkerProps) {
  const [hovered, setHovered] = useState(false);
  const date = parseDateString(certification.date);
  if (!date) return null;
  const cx = scales.xScale(date);

  return (
    <motion.g
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.35, ease: [0.22, 0.36, 0.27, 1] }}
      style={{ cursor: "pointer", transformOrigin: `${cx}px ${y}px` }}
      onMouseEnter={(e) => {
        setHovered(true);
        onHover(certification, { x: e.clientX, y: e.clientY });
      }}
      onMouseLeave={() => {
        setHovered(false);
        onHover(null, null);
      }}
    >
      {/* Vertical pin to the axis baseline */}
      <line
        x1={cx}
        x2={cx}
        y1={y - 18}
        y2={y - SIZE}
        stroke="var(--color-rule)"
        strokeWidth={0.5}
        opacity={hovered ? 0.8 : 0.35}
        style={{ transition: "opacity 220ms ease" }}
      />
      {/* Outlined diamond */}
      <polygon
        points={`${cx},${y - SIZE} ${cx + SIZE},${y} ${cx},${y + SIZE} ${cx - SIZE},${y}`}
        fill="var(--color-ink)"
        stroke="var(--color-copper)"
        strokeWidth={1.4}
        opacity={hovered ? 1 : 0.95}
      />
      {/* Inner accent dot, only on hover */}
      {hovered && (
        <circle
          cx={cx}
          cy={y}
          r={1.6}
          fill="var(--color-copper)"
        />
      )}
    </motion.g>
  );
}
